import { action as mobxAction, makeAutoObservable, toJS, observable, computed } from "mobx"
import { FullS3DomainOptions, GlobalConfig, IMenu, MenuItem, WorksStoreProps } from "./types"
import globalData from "./globalData"
import globalEnumInside from "./globalEnumInside"
import Api from "@/server"
import { get, set } from "@/utils/tools"
import * as 枚举值 from "@/types/ConstantRecord"
import { replaceDoubleSlashes, webpSupport } from "./tools"
import errorReport from "@/utils/sentry"
import { EventEmitter } from "@/utils/event"

export class WorksStore {
    constructor(props: WorksStoreProps) {
        makeAutoObservable(this)
    }

    @observable supportWebp = webpSupport()

    @observable token: string = ""

    @observable isLogin: boolean = false

    @observable tagWidth: number = 600

    @observable tagWidthHalf: number = 300

    @observable userInfo: Record<string, any> = {}

    // 控制页面开发用数据显示隐藏的值
    @observable _showField: boolean = false
    // 用来判断是否拉取用户信息已完成
    @observable _isInitUserInfoFinish: boolean = false

    @observable menus: Array<IMenu> = []

    @observable globalEnum: Record<string, any> = globalData
    @observable globalEnumInside: Record<string, any> = globalEnumInside
    @observable globalEnumKV: Record<string, any> = {}

    @observable isAllPerm = false

    @observable globalConfig: GlobalConfig = {
        imageDomain: process.env.REACT_APP_ENV === "PROD" ? "https://mpr-pro-6767.s3.ap-east-1.amazonaws.com/" : "https://mpr-source.s3.ap-east-1.amazonaws.com/",
        fileDomain: process.env.REACT_APP_ENV === "PROD" ?  "https://mpr-pro-6767.s3.ap-east-1.amazonaws.com/" : "https://mpr-source.s3.ap-east-1.amazonaws.com/",
    }

    /** 用户的权限 */
    @computed get permMap() {
        let permIds = []
        if (this.userInfo && this.userInfo.menuPermList) {
            permIds = this.userInfo.menuPermList.map((item: any) => item.permCode)
        }
        return permIds.reduce((acc: Record<string, boolean>, cur: string) => {
            acc[cur] = true
            return acc
        }, {})
    }

    @mobxAction
    setAllPerm = () => {
        const routers = Object.values(globalEnumInside.PERM_KEY)
        const permIds = this.userInfo.menuPermList.map((item: any) => item.permCode)
        this.userInfo.permCodes = permIds
        set(this.userInfo, "permCodes", routers)
    }

    @mobxAction
    switchField = () => {
        this._showField = !this._showField
    }

    @mobxAction
    setInitUserInfoFinish = (result: boolean) => {
        this._isInitUserInfoFinish = result
    }

    @mobxAction
    init = (arr: Array<IMenu>) => {
        this.menus = arr
    }

    @mobxAction
    initStore = () => {
        this.token = localStorage.getItem("token") || ""
        this.isLogin = !!this.token
        const loginAndRedirect = async () => {
            try {
                const data: any = await this.getUserInfo()
                if (!data) {
                    window.location.hash = "#/"
                    return
                }
            } catch (e) {
                window.location.hash = "#/"
            }
        }
        if (this.isLogin) {
            loginAndRedirect()
        } else {
            window.location.hash = "#/"
        }
        setTimeout(() => {
            console.log(` 状态机 `, toJS(this))
        }, 8000)
    }

    @mobxAction
    login = (token: string) => {
        this.setToken(token)
        this.isLogin = true
    }

    @mobxAction
    logout = () => {
        this.setUserInfo({}) // 清空权限，用户定时刷新报错问题
        EventEmitter.emit("clearItemsWhenLogout", {})
        EventEmitter.emit("stopLoopGetNotification", {})
        this.token = ""
        localStorage.removeItem("token")
        this.isLogin = false
        this._isInitUserInfoFinish = false
    }

    @mobxAction
    setImageDomain = (url: string) => {
        this.globalConfig.imageDomain = url
    }

    @mobxAction
    setGlobalEnum = (data: any) => {
        const 内部枚举值: Record<string, Array<{ label: string; value: string }>> = {}
        for (const [key, items] of Object.entries(枚举值)) {
            const map: Array<{ label: string; value: string }> = []

            for (const [k, v] of Object.entries(items)) {
                // Ensure `v` is a string before checking with includes()
                if (typeof v === "string" && !Object.keys(items).includes(v)) {
                    map.push({ label: k, value: v })
                } else {
                    map.push({ label: k, value: String(v) })
                }
            }

            内部枚举值[key] = map
        }
        data["内部枚举值"] = 内部枚举值
        this.globalEnum = data
    }

    @mobxAction
    setGlobalEnumKV = (data: any) => {
        const 内部枚举值: Record<string, Record<string, string>> = {}
        for (const [key, items] of Object.entries(枚举值)) {
            const map: Record<string, string> = {}
            for (const [k, v] of Object.entries(items)) {
                if (typeof v === "string" && !Object.keys(items).includes(v)) {
                    map[v] = k
                } else if (!Object.keys(items).includes(String(v))) {
                    map[String(v)] = k
                }
            }
            内部枚举值[key] = map
        }

        data["内部枚举值"] = 内部枚举值
        this.globalEnumKV = data
    }

    getImageUrl = (url: string, options: FullS3DomainOptions = {}) => {
        const { autoTransform = false, isLocal = false } = options
        if (url === "") {
            return url
        }
        if (url.includes("http")) {
            return url
        }
        if (url.includes("data:image")) {
            return url
        }
        if (autoTransform && this.supportWebp) {
            url = url.replace(/\.jpg|\.png|\.jpeg/, ".webp")
        }
        const fullUrl = `/${url}`.replace("//", "/")
        if (isLocal) {
            return `./images${fullUrl}`
        }
        const s3Domain = this.globalConfig.imageDomain
        const result = replaceDoubleSlashes(`${s3Domain}${fullUrl}`)
        return result
    }

    getFileUrl = (url: string) => {
        return `${this.globalConfig.fileDomain}${String(url).replace(/^\//, "")}`
    }

    checkPerm = (permKey: string) => {
        const permIds = this.userInfo.menuPermList.map((item: any) => item.permCode)
        return permIds.includes(permKey)
    }

    @mobxAction
    setToken = (token: string) => {
        this.token = token
        localStorage.setItem("token", token)
    }

    @mobxAction
    setUserInfo = (userInfo: any) => {
        this.userInfo = userInfo
    }

    @mobxAction
    getUserInfo = async () => {
        const userInfo = await Api.post(`/common/userInfo`, {})
        if (userInfo) {
            this.setUserInfo(userInfo)
            this._isInitUserInfoFinish = true
            errorReport.init({ userInfo })
        } else {
            this._isInitUserInfoFinish = false
        }
        return userInfo
    }
}

export const workStore = new WorksStore({})
